import React from 'react';
import Flexbox from 'flexbox-react';
import { cx, css } from 'emotion';
import Constants from 'utils/Constants';
import ProcessUtils from 'utils/ProcessUtils';
import ComponentUtils from 'utils/ComponentUtils';
import VariableRequiredOptionsForm from 'components/core/forms/VariableRequiredOptionsForm';
import InfoBoxLabel from 'components/core/InfoBoxLabel';
import Toolbar from 'components/core/toolbar/Toolbar';
import ToolbarItem from 'components/core/toolbar/ToolbarItem';
import ProcessWizardPage from 'components/page/process/wizard/ProcessWizardPage';
import RetrievedVariablesList from 'components/page/process/variableRetrieval/RetrievedVariablesList';
import InputDatastreamsField from 'components/core/fields/InputDatastreamsField';
import { colorBorderGrey } from 'styles/base';
import InfoMessages from 'components/core/help/InfoMessages';

const listStyle = css`
    height: 0;
    flex-grow:1;
    .items {
        overflow-y: auto;
        overflow-x: hidden;
        max-height: 100%;
    }
`;

export default class InputsPage extends ProcessWizardPage {
    static defaultProps = {
        title: 'Define Inputs',
        description: 'Specify the variables you wish to consolidate into a single data stream.'
    };

    getVariableRequired() {
        return ProcessUtils.getGlobalVariableProperty(this.props.process, 'variable_required', false);
    }
   
    getRetrieveQCVariable() {
        return ProcessUtils.getGlobalVariableProperty(this.props.process, 'retrieve_qc_variable', true);
    }

    qetQCVariableRequired() {
        return ProcessUtils.getGlobalVariableProperty(this.props.process, 'qc_variable_required', false);
    }

    sortVariableIds() {
        const sortedIds = ProcessUtils.sortRetrievedVariablesByDataset(this.props.process);
        this.sortedRetrievedVariableIds = sortedIds.sortedRetrievedVariableIds;
        this.sortedInputDatasetIds = sortedIds.sortedInputDatasetIds;
    }

    makeGlobalVariableChange(changeHandler) {
        const editor = ProcessUtils.getEditor(this.props.process);
        ProcessUtils.visitRetrievedVariables(editor.process, (retrievedVariable) => {
            changeHandler(retrievedVariable);
        });
        editor.commit();
    }

    onDeleteRetrievedVariables = (selectedItems) => {
        const editor = ProcessUtils.getEditor(this.props.process);
        editor.deleteEntities('variable_retrieval.retrieved_variables', selectedItems);
        editor.removeUnusedCoordinateSystems();
        editor.commit();
    };

    onVariableRequiredChanged = (value) => {
        this.makeGlobalVariableChange((retrievedVariable) => {
            retrievedVariable.variable_required = value ? 1 : 0;
        });
    };

    onRetrieveQCVariableChanged = (value) => {
        this.makeGlobalVariableChange((retrievedVariable) => {
            retrievedVariable.retrieve_qc_variable = value ? 1 : 0;
        });
    };

    onQCVariableRequiredChanged = (value) => {
        this.makeGlobalVariableChange((retrievedVariable) => {
            retrievedVariable.qc_variable_required = value ? 1 : 0;
        });
    };

    renderVariableRetrievalForm = () => {
        this.sortVariableIds();
        const listStyles = css`
            .entity-list {
                min-width: 275px;
                background-color: white;
                border: solid 1px ${colorBorderGrey};
                margin-top: 5px;
                margin-right: 5px;
            }
            .items div:first-child div:first-child {
                padding-top: 3px !important;
            }
            .items div:last-child .list-item {
                padding-bottom: 6px;
            }
            .infobox {
                align-self: flex-start !important;
            }
        `;
        const toolbarButtonStyles = css`
            flex-direction:column!important;
            align-items:left!important;
            justify-content: flex-start !important;
            .toolbar-item {
                width:100%;
            }
            .toolbar-item button {
                width:100%;
            }
        `;
        // Don't show the radios until we have selected some variables
        const varNames = Object.keys(this.props.process.variable_retrieval.retrieved_variables);
        const radioClassName = (varNames.length > 0) ? css`display: block;` : css`display: none;`;
        const disabledMessage = ComponentUtils.getDisabledMessage(this.props.process.deprecated);

        return (
            <Flexbox flexDirection="row">
                <Flexbox flexDirection="column" padding="0 10px" className={`entity-box retrieved-variables ${cx(listStyles)}`} flexGrow={1}>
                    <InfoBoxLabel
                        label="Input Variables"
                        message={InfoMessages.inputVariables}
                    />
                    <RetrievedVariablesList
                        id={'retrieved-variables'}
                        sortedIds={this.sortedRetrievedVariableIds}
                        data={this.props.process.variable_retrieval.retrieved_variables}
                        process={this.props.process}
                        type={Constants.TYPE_RETRIEVED_VARIABLE}
                        filterable={false}
                        processErrors={{}}
                        baseFieldPath={['variable_retrieval', 'retrieved_variables']}
                        emptyDataMsg="No variables selected"
                        showDataSources
                        showUnits
                        hideInputDatasetName
                        hideBadges
                        asideChildren
                        reverse
                        className={cx('retrieved-variable-list', listStyle)}
                    >
                        <Toolbar className={cx(toolbarButtonStyles)}>
                            <ToolbarItem
                                title="Add Variables"
                                clickHandler={ProcessUtils.newRetrievedVariableFunction(this.props.process)}
                                button
                                enabled={this.props.hasWritePermissions}
                                disabledMessage={disabledMessage}
                                className={css`margin-bottom:5px;`}
                            />
                            <ToolbarItem
                                title="Remove Selected"
                                clickHandler={this.onDeleteRetrievedVariables}
                                button
                                enabled={this.props.hasWritePermissions}
                                disabledMessage={disabledMessage}
                                selectionDependent
                            />
                        </Toolbar>
                    </RetrievedVariablesList>
                </Flexbox>

                <Flexbox flexDirection="column" padding="0 10px" margin="22px 0 0 60px" maxWidth="300px">
                    <VariableRequiredOptionsForm
                        className={radioClassName}
                        isDisabled={!this.props.hasWritePermissions}
                        process={this.props.process}
                        variableRequired={this.getVariableRequired()}
                        retrieveQCVariable={this.getRetrieveQCVariable()}
                        qcVariableRequired={this.qetQCVariableRequired()}
                        onVariableRequiredChanged={this.onVariableRequiredChanged}
                        onRetrieveQCVariableChanged={this.onRetrieveQCVariableChanged}
                        onQCVariableRequiredChanged={this.onQCVariableRequiredChanged}
                        labelWrap
                    />
                </Flexbox>
            </Flexbox>
        );
    };

    renderInputDatastreamForm = () => {
        return (
            <InputDatastreamsField
                process={this.props.process}
                processErrors={this.props.processErrors}
                dods={this.props.dods}
            />
        );
    };

    renderFormWithProcess() {
        return this.renderVariableRetrievalForm();
    }
}



// WEBPACK FOOTER //
// ./src/components/page/process/wizard/InputsPage.js